/*eslint-disable*/
import React, { useEffect, useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Grid, Button, Typography } from "@mui/material";
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { isSuperAdmin } from "../../../../helpers/user";
// theme css file
import { SIX_MONTHS_BEFORE_DATE } from '../../userActivities.constants';

import TextInputField from "../../../../components/textInputField/TextInputField";
import InfoIconWithText from "../../../../components/infoIconWithText";
import { validateFilters, isMandatoryFieldSelected  } from "./filters.helpers";
import styles from "./filters.module.scss";
import { removeEmptyProperties } from "../../../../helpers/general"
import { useTranslation } from 'react-i18next'
import { useIsMobileView } from "../../../../utils/isMobileView";

const searchStyle = {
  '&:disabled': {
    backgroundColor: 'rgba(102, 90, 71, 0.15) !important',
  }
}

const isValidEmail = (email = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

const normalizeEmail = (email = "") =>
  String(email)
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();

function Filters({ filters, setFilters, getUserActivities, variant = 'api', autoSearch, setAutoSearch }) {
  const [errors, setErrors] = useState({})
  const [shouldDisableSearch, setShouldDisableSearch] = useState(false);
  const isSecurity = variant === 'security';
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()

  const emailTrimmed = (filters.email ?? "").trim();
  const emailIsPresent = emailTrimmed.length > 0;
  const emailIsValid = isValidEmail(emailTrimmed);

  const disableSearch =
    shouldDisableSearch ||
    !emailIsPresent ||
    !emailIsValid ||
    Object.keys(removeEmptyProperties(errors)).length > 0;

  const handleFilterChange = (name, value) => {
  const nextValue = name === 'email' ? normalizeEmail(value) : value;

  const updatedFilters = { ...filters, [name]: nextValue };
  const errorMsgs = validateFilters(updatedFilters);

  if (name === "email") {
    const e = updatedFilters.email;
    if (!e) {
      errorMsgs.email = t('user-activities-email-required');
    } else if (!isValidEmail(e)) {
      errorMsgs.email = t('user-activities-email-invalid');
    } else {
      delete errorMsgs.email;
    }
  }

  setErrors(errorMsgs);
  setFilters(updatedFilters);
};

  const handleSearch = () => {
  const email = normalizeEmail(filters.email);

  if (email !== filters.email) {
    setFilters(prev => ({ ...prev, email }));
  }

  if (!email) {
    setErrors(prev => ({ ...prev, email: t('user-activities-email-required') }));
    return;
  }
  if (!isValidEmail(email)) {
    setErrors(prev => ({ ...prev, email: t('user-activities-email-invalid') }));
    return;
  }

  setShouldDisableSearch(true);

  const errorMsgs = validateFilters({ ...filters, email });
  if (Object.keys(removeEmptyProperties(errorMsgs)).length >= 1) {
    setErrors(errorMsgs);
    return;
  }

  setErrors({});
  getUserActivities();
};


  useEffect(() => {
    if(!autoSearch){
      setShouldDisableSearch(false)
    }
  }, [filters])

  useEffect(()=>{
    if(!autoSearch) return

    handleSearch()
    setAutoSearch(false)
  }, [autoSearch])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <Grid container spacing={2} style={{marginTop: 0}} className={styles.gridContainer}>
            <Grid item className={styles.gridItem}>
              <TextInputField
                placeholder="example@email.com"
                label={<InfoIconWithText label={t('user-activities-email')} helpText={t('user-activities-email-help-text')} />}
                disabled={!isSecurity && !isSuperAdmin()}
                value={filters.email}
                styles={{ width: isMobileView ? "100%" : "300px" }}
                error={errors.email}
                onChange={e => handleFilterChange('email', e.target.value)}
                required
                requiredWithoutText
                textFieldClassName={styles.emailInput}
                labelFieldClassName={styles.inputLabel}
              />
            </Grid>
            <Grid style={{ marginTop: "-12px" }} item className={styles.gridItem}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={[
                  'DatePicker',
                  'DesktopDatePicker',
                ]} className={{ display: 'flex' }}>
                  <DemoItem label={
                     <InfoIconWithText label={t('user-activities-start-date')} helpText={t('user-activities-start-date-help-text')} labelClassName={styles.inputLabel}/>
                  }
                  >
                    <DateTimePicker
                      value={filters.startTime}
                      onChange={(newDate) => handleFilterChange('startTime', newDate)}
                      minDate={SIX_MONTHS_BEFORE_DATE}
                      disableFuture={true}
                      inputFormat="yyyy-MM-dd'T'HH:mm"
                      inputProps={{
                        style: { fontSize: '16px' } // Adjust font size here
                      }}
                    />
                  </DemoItem>
                  <DemoItem label={
                    <InfoIconWithText style ={{fontSize:"10px"}} label={t('user-activities-end-date')} helpText={t('user-activities-end-date-help-text')} labelClassName={styles.inputLabel}/>
                  }>
                    <DateTimePicker
                      value={filters.endTime}
                      onChange={(newDate) => handleFilterChange('endTime', newDate)}
                      disableFuture={true} 
                      inputFormat="yyyy-MM-dd'T'HH:mm"
                      inputProps={{
                        style: { fontSize: '16px' } // Adjust font size here
                      }}
                      />
                    {errors.endTime && (
                      <Typography variant="body2" color="error">
                        {errors.endTime}
                      </Typography>
                    )}
                  </DemoItem>
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid style={{ marginTop: 15 }} container>
            <Grid item xs={8} style={{ flexGrow: 1, maxWidth: '100%' }}>
              <Button
                onClick={handleSearch}
                className={styles.searchBtn}
                variant="contained"
                disabled={disableSearch}
                sx={{ ...searchStyle }} >
                {t('user-activities-search')}
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
}

export default Filters;
